import { useEffect, useState } from "react";
import { Bell, Check, CheckCheck, Trash2, AlertTriangle, ShieldOff, UserPlus, Settings, Info } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";

const TYPE_ICONS = {
  user_created: UserPlus,
  user_status: ShieldOff,
  login_failed: AlertTriangle,
  settings_changed: Settings,
  unauthorized: AlertTriangle,
  default: Info,
};

const PRIORITY_COLOR = {
  info: "bg-blue-50 text-[#101876] border-blue-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  critical: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function AdminNotificationsPage() {
  const [notes, setNotes] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unread

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === "unread" ? { unread_only: true } : {};
      const { data } = await api.get("/admin/notifications", { params });
      setNotes(data.data.notifications);
      setUnread(data.data.unread_count);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const markRead = async (id) => {
    try {
      await api.patch(`/admin/notifications/${id}/read`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const markAll = async () => {
    try {
      await api.patch("/admin/notifications/mark-all-read");
      toast.success("All notifications marked read.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/notifications/${id}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardLayout title="Notifications" breadcrumbs={["Platform", "Notifications"]}>
      <div className="max-w-3xl space-y-4" data-testid={ADMIN.notificationsRoot}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#101876]/5 px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#101876]">
              <Bell className="h-3.5 w-3.5" /> {unread} unread
            </div>
            <div className="inline-flex overflow-hidden rounded-md border border-slate-200 bg-white text-xs font-semibold">
              <button onClick={() => setFilter("all")} className={`px-3 py-1.5 ${filter === "all" ? "bg-[#101876] text-white" : "text-slate-600 hover:bg-slate-50"}`}>All</button>
              <button onClick={() => setFilter("unread")} className={`px-3 py-1.5 ${filter === "unread" ? "bg-[#101876] text-white" : "text-slate-600 hover:bg-slate-50"}`}>Unread</button>
            </div>
          </div>
          <button
            onClick={markAll}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#101876]/40 hover:text-[#101876]"
            data-testid={ADMIN.notificationsMarkAll}
          >
            <CheckCheck className="h-3.5 w-3.5" /> Mark all read
          </button>
        </div>

        {loading ? (
          <LoadingBlock label="Loading notifications…" />
        ) : notes.length === 0 ? (
          <EmptyState title="You're all caught up" description="No notifications right now." icon={Bell} />
        ) : (
          <ul className="space-y-2">
            {notes.map((n) => {
              const Icon = TYPE_ICONS[n.notification_type] || TYPE_ICONS.default;
              const color = PRIORITY_COLOR[n.priority] || PRIORITY_COLOR.info;
              return (
                <li key={n.id} className={`flex items-start gap-4 rounded-2xl border bg-white p-4 ${n.is_read ? "border-slate-200" : "border-[#101876]/30 shadow-sm"}`} data-testid={ADMIN.notificationRow(n.id)}>
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border ${color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-bold text-slate-900">{n.title}</div>
                      {!n.is_read && <span className="h-1.5 w-1.5 rounded-full bg-[#329112]" />}
                    </div>
                    <div className="mt-0.5 text-sm text-slate-600">{n.message}</div>
                    <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-400">
                      {n.notification_type.replace(/_/g, " ")} · {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {!n.is_read && (
                      <button onClick={() => markRead(n.id)} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-[#101876]"
                              data-testid={ADMIN.notificationMarkRead(n.id)} title="Mark as read">
                        <Check className="h-4 w-4" />
                      </button>
                    )}
                    <button onClick={() => remove(n.id)} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-rose-50 hover:text-rose-600"
                            data-testid={ADMIN.notificationDelete(n.id)} title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </DashboardLayout>
  );
}
